import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import type Forum from "messaging/forums/forum";
import Message from "messaging/message";
import { isUnreadBy } from "messaging/read-receipts/unread-rule";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useEffect, useRef, useState } from "react";

type ForumReadStateInput = {
  forum: Forum | undefined;
  messages: readonly Message[];
  userId: Guid | undefined;
  /**
   * The conversation is on screen. Opening it marks what it holds read, as opening the proposal pane
   * does; a pane kept mounted behind a closed drawer marks nothing.
   */
  isOpen: boolean;
  /** The messages are still loading: the sweep waits for them. */
  isLoading: boolean;
  /**
   * The server's count of the reader's unread messages in the conversation: all of them, loaded or not.
   * Undefined until the server has sent one.
   */
  serverUnreadCount?: number;
};

export type ForumReadState = {
  isUnread: (message: Message) => boolean;
  /**
   * How many messages in the conversation the reader has not read: the server's count, which takes in
   * the ones not loaded yet. The loaded ones until the server has sent a count.
   */
  unreadCount: number;
  /** How many of the conversation's loaded messages the reader has not read. */
  loadedUnreadCount: number;
  /** A read-state write is in flight for this message. */
  isUpdating: (message: Message) => boolean;
  /** Marks the message read when it is unread, unread when it is read. */
  toggle: (message: Message) => void;
  markAllRead: () => void;
};

/**
 * Read receipts for the work agreement's Correspondence pane, as the proposal pane keeps them.
 *
 * - Opening a conversation marks the messages it holds read. One that arrives while it is open stays
 *   unread, and counts, until the reader marks it, marks all read, or opens the conversation again.
 * - A message can be marked read or unread on its own. A system notice can too: it counts.
 * - Mark all read marks every unread message in the conversation, on the server, loaded or not.
 *
 * "Unread" is `isUnreadBy` in `messaging/read-receipts/unread-rule`, the server's rule (#5828). Each
 * write is for the calling reader only.
 */
export default function useForumReadState(input: Readonly<ForumReadStateInput>): ForumReadState {
  const { forum, messages, userId, isOpen, isLoading, serverUnreadCount } = input;
  const dispatch = useAttorneyHubDispatch();
  const [updatingIds, setUpdatingIds] = useState<ReadonlySet<string>>(new Set());
  const sweptForumId = useRef<string | undefined>(undefined);
  /** The oldest message the visit has swept so far: anything loaded from before it is an older page. */
  const sweptFrom = useRef<AHDate | undefined>(undefined);

  const unread = messages.filter((message) => isUnreadBy(message, userId));

  function write(targets: readonly Message[], read: boolean) {
    if (targets.length === 0) return;
    const ids = targets.map((message) => message.id.value);
    setUpdatingIds((current) => new Set([...current, ...ids]));
    void writeReadReceipts(dispatch, targets, read).then(() =>
      setUpdatingIds((current) => new Set([...current].filter((id) => !ids.includes(id)))),
    );
  }

  const forumId = forum?.id?.value;
  // Any reader is swept, whether or not they may write: a read receipt is the reader's own record
  // and the server takes it from anybody who may read the conversation, so somebody asked to lead a
  // team, or somebody who left the work, sees their unread count clear like everybody else (#6094).
  // Whether they may write decides only the message box -- see `Forum.takesMessagesFromReader`.
  //
  // A conversation opens on its newest page (#6760), so the visit goes on to mark what it loads from
  // further back: an older page the reader scrolls up to, and the pages a link loads to reach the oldest
  // unread message (#6585). Those were left unread, and every later visit by the link was taken back to
  // them. A message that arrives during the visit is newer than anything swept, and stays unread.
  useEffect(() => {
    if (!isOpen || isLoading || !forumId) return;
    const sameVisit = sweptForumId.current === forumId;
    const from = sweptFrom.current;
    const targets = messages.filter(
      (message) => isUnreadBy(message, userId) && (!sameVisit || isOlderThan(message, from)),
    );
    sweptForumId.current = forumId;
    sweptFrom.current = oldestCreated(messages) ?? (sameVisit ? from : undefined);
    // Nobody asked for this write, so a refusal is not theirs to hear about: the messages stay
    // unread, and the next visit tries again (#6094).
    if (targets.length > 0) void writeReadReceipts(dispatch, targets, true, { announceFailure: false });
  }, [dispatch, forumId, isLoading, isOpen, messages, userId]);

  // Closing the pane ends the visit: opening it again marks what arrived in the meantime.
  useEffect(() => {
    if (isOpen) return;
    sweptForumId.current = undefined;
    sweptFrom.current = undefined;
  }, [isOpen]);

  const unreadCount = serverUnreadCount ?? unread.length;
  return {
    isUnread: (message) => isUnreadBy(message, userId),
    unreadCount,
    loadedUnreadCount: unread.length,
    isUpdating: (message) => updatingIds.has(message.id.value),
    toggle: (message) => write([message], !message.isReadByUser(userId)),
    markAllRead: () => {
      if (!forumId || !userId || (unreadCount === 0 && unread.length === 0)) return;
      void markForumRead(dispatch, forumId, userId);
    },
  };
}

/** The message was written before `boundary`. Nothing is older than a boundary not yet set. */
function isOlderThan(message: Message, boundary: AHDate | undefined): boolean {
  if (!boundary || !message.created) return false;
  return message.created.isBefore(boundary);
}

function oldestCreated(messages: readonly Message[]): AHDate | undefined {
  let oldest: AHDate | undefined;
  for (const message of messages) {
    if (message.created && (!oldest || message.created.isBefore(oldest))) oldest = message.created;
  }
  return oldest;
}

/**
 * Marks every unread message in the forum read on the server. Outside the hook for the same reason as
 * `writeReadReceipts`.
 */
async function markForumRead(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  forumId: string,
  readerId: Guid,
): Promise<void> {
  try {
    await dispatch(messagingApi.endpoints.markForumRead.initiate({ forumId, readerId })).unwrap();
  } catch (error) {
    console.error("Failed to mark the conversation read", error);
    showErrorSnackbar(error, { doing: "marking the conversation read", operation: "messaging.mark-forum-read" });
  }
}

/**
 * Sends one bulk write of read receipts. Outside the hook because it needs a `catch`, and the React
 * Compiler skips a component whose code it cannot lower.
 *
 * A failure the reader caused, by marking a message, is announced. One the page caused, by marking
 * what an opened conversation holds, is only logged.
 */
async function writeReadReceipts(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  targets: readonly Message[],
  read: boolean,
  { announceFailure = true }: { announceFailure?: boolean } = {},
): Promise<void> {
  const receipts = targets.map((message) => {
    const receipt = Message.create({ ...message, forum: message.forum });
    if (read) receipt.markRead();
    else receipt.markUnread();
    return receipt;
  });
  try {
    await dispatch(messagingApi.endpoints.createBulkMessages.initiate({ messages: receipts })).unwrap();
  } catch (error) {
    if (!announceFailure) {
      console.warn("Could not mark the opened conversation read", error);
      return;
    }
    console.error("Failed to write read receipts", error);
    showErrorSnackbar(error, {
      doing: read ? "marking that read" : "marking that unread",
      operation: read ? "messaging.mark-read" : "messaging.mark-unread",
    });
  }
}
